import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Inject, Injectable, NgZone, Optional
} from '@angular/core';

import {
  AuthConfig, DateTimeProvider, OAuthLogger, OAuthService, OAuthStorage, UrlHelperService, ValidationHandler
} from 'angular-oauth2-oidc';

/**
 * Custom OAuth Service, with a bug fix to silent refresh event listener.
 *
 * The default OAuthService (as of angular-oauth2-oidc@10.0.3) has a bug in it's silent refresh postMessage listener,
 * which causes it to attempt to handle messages being sent by scripts unrelated to silent refresh, causing early termination
 * of the silent refresh process. That gets us all sorts of out of sync with nonce values in session storage, etc.
 *
 * ! when upgrading angular-oauth2-oidc, replace the implementation of `setupSilentRefreshEventListener` to match
 * ! the new version, but ensure that it continues bailing early on undefined messages.
 */
@Injectable()
export class AuthTokenOAuthService extends OAuthService {
  constructor(
    protected override ngZone: NgZone,
    protected override http: HttpClient,
    @Optional() storage: OAuthStorage,
    @Optional() tokenValidationHandler: ValidationHandler,
    @Optional() protected override config: AuthConfig,
    protected override urlHelper: UrlHelperService,
    protected override logger: OAuthLogger,
    @Inject(DOCUMENT) document: any,
    @Inject(DateTimeProvider) dateTimeService: DateTimeProvider
  ) {
    super(ngZone, http, storage, tokenValidationHandler, config, urlHelper, logger, null, document, dateTimeService);
  }

  /**
   * installs the correct silent refresh `postMessage` event handler, so that response from
   * the silent refresh iframe triggers oauth events.
   *
   * this is a custom override to fix a bug
   * setup silent refresh event listener; called as part of silent refresh.
   *
   * this implementation is a copy of the implementation in angular-oauth2-oidc@10.0.3
   * ! when upgrading this library, replace the body, but maintain the message undefined bugfix
   */
  protected override setupSilentRefreshEventListener(): void {
    this.removeSilentRefreshEventListener();

    this.silentRefreshPostMessageEventListener = (e: MessageEvent) => {
      const message = this.processMessageEventMessage(e);

      // PE-9567 (and others)
      // if the message is undefined, it's almost assuredly coming from a script that's unrelated
      // to oauth (stripe, webpack, etc.) the original implementation of this method does not check
      // for this case, and sends undefined to the `tryLogin` method, causing spurious oauth events,
      // which can seriously mess up the current oauth flow.
      //
      // instead, just ignore the message altogether, so that only messages coming
      // from the silent refresh page are handled here.
      if (message == null) { return }

      this.tryLogin({
        customHashFragment: message,
        preventClearHashAfterLogin: true,
        customRedirectUri: this.silentRefreshRedirectUri || this.redirectUri
      }).catch((err) => this.debug('tryLogin during silent refresh failed', err));
    };

    window.addEventListener(
      'message',
      this.silentRefreshPostMessageEventListener
    );
  }
}
